import React, { useState, useEffect } from 'react';
import { obtenerCartas } from '../../services/cartaCompromisoService';
import { toast } from 'react-toastify';

const CartaCompromiso = ({ onAceptar, onRechazar, cartaAceptada, planSeleccionado }) => {
    const [cartas, setCartas] = useState([]);
    const [cartaSeleccionada, setCartaSeleccionada] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        cargarCartas();
    }, []);

    const cargarCartas = async () => {
        try {
            setLoading(true);
            const response = await obtenerCartas();
            if (response.success && response.data && response.data.length > 0) {
                // Filtrar solo las cartas que coincidan con "Carta de compromiso"
                const cartasCompromiso = response.data.filter(carta => 
                    carta.nombre && carta.nombre.toLowerCase().includes('carta de compromiso')
                );
                
                if (cartasCompromiso.length > 0) {
                    setCartas(cartasCompromiso);
                    // Seleccionar la primera carta de compromiso disponible
                    setCartaSeleccionada(cartasCompromiso[0]);
                } else {
                    setError('No se encontró ninguna carta con el nombre "Carta de compromiso"');
                }
            } else {
                setError('No se encontraron cartas de compromiso disponibles');
            }
        } catch (error) {
            console.error('Error al cargar cartas:', error);
            setError('Error al cargar las cartas de compromiso');
            toast.error('Error al cargar las cartas de compromiso', {
                position: 'top-right',
                autoClose: 3000,
                style: { 
                    marginTop: '10px',
                    fontFamily: '"Exo 2", sans-serif',
                    fontWeight: 500
                }
            });
        } finally {
            setLoading(false);
        }
    };

    const handleAceptar = () => {
        if (cartaSeleccionada) {
            onAceptar(cartaSeleccionada.id_carta);
            toast.success('Carta de compromiso aceptada', {
                position: 'top-right',
                autoClose: 2000,
                style: { 
                    marginTop: '10px',
                    fontFamily: '"Exo 2", sans-serif',
                    fontWeight: 500
                }
            });
        }
    };

    const handleRechazar = () => {
        onRechazar();
        toast.warning('Debe aceptar la carta de compromiso para continuar con la matrícula', {
            position: 'top-right',
            autoClose: 4000,
            style: { 
                marginTop: '10px',
                fontFamily: '"Exo 2", sans-serif',
                fontWeight: 500
            }
        });
    };

    if (loading) {
        return (
            <div className="bg-white p-6 rounded-lg shadow-md">
                <div className="flex justify-center items-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                    <span className="ml-3 text-gray-600">Cargando carta de compromiso...</span>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="bg-white p-6 rounded-lg shadow-md">
                <div className="bg-red-50 border border-red-200 rounded-md p-4">
                    <div className="flex">
                        <div className="flex-shrink-0">
                            <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                            </svg>
                        </div>
                        <div className="ml-3">
                            <h3 className="text-sm font-medium text-red-800">Error</h3>
                            <div className="mt-2 text-sm text-red-700">
                                <p>{error}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (!cartaSeleccionada) {
        return (
            <div className="bg-white p-6 rounded-lg shadow-md">
                <div className="text-center py-8">
                    <p className="text-gray-600">No hay cartas de compromiso disponibles</p>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-white p-6 rounded-lg shadow-md">
            <h3 className="text-xl font-semibold text-gray-800 mb-4 text-center">
                Carta de Compromiso
            </h3>
            
            <div className="bg-gray-50 p-4 rounded-lg mb-6">
                <h4 className="font-medium text-gray-800 mb-3">{cartaSeleccionada.nombre}</h4>
                
                {cartaSeleccionada.descripcion && (
                    <div className="mb-4">
                        <p className="text-gray-700 leading-relaxed whitespace-pre-line">
                            {cartaSeleccionada.descripcion}
                        </p>
                    </div>
                )}
                
                {cartaSeleccionada.clausulas && cartaSeleccionada.clausulas.length > 0 && (
                    <div>
                        <h5 className="font-medium text-gray-800 mb-3">Cláusulas:</h5>
                        <ol className="list-decimal list-inside space-y-2">
                            {cartaSeleccionada.clausulas.map((clausula, index) => {
                                const textoClausula = clausula.clausula;
                                const contieneHorarios = textoClausula.includes('Atenderé que mi hijo/a') || 
                                                       textoClausula.includes('hora de entrada') || 
                                                       textoClausula.includes('hora de salida');
                                
                                return (
                                    <li key={clausula.id_clausula || index} className="text-gray-700 leading-relaxed">
                                        {textoClausula}
                                        {contieneHorarios && planSeleccionado && (
                                            <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-md">
                                                <p className="text-sm font-medium text-blue-800 mb-1">
                                                    Horarios del plan seleccionado:
                                                </p>
                                                <div className="text-sm text-blue-700">
                                                    <p><strong>Plan:</strong> {planSeleccionado.nombre}</p>
                                                    <p><strong>Hora de entrada:</strong> {planSeleccionado.hora_inicio}</p>
                                                    <p><strong>Hora de salida:</strong> {planSeleccionado.hora_fin}</p>
                                                </div>
                                            </div>
                                        )}
                                    </li>
                                );
                            })}
                        </ol>
                    </div>
                )}
            </div>

            <div className="border-t pt-4">
                <p className="text-sm text-gray-600 mb-4 text-center">
                    Para continuar con el proceso de matrícula, debe leer y aceptar la carta de compromiso.
                </p>
                
                {cartaAceptada ? (
                    <div className="bg-green-50 border border-green-200 rounded-md p-4">
                        <div className="flex items-center">
                            <svg className="h-5 w-5 text-green-400 mr-2" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                            <span className="text-green-800 font-medium">Carta de compromiso aceptada</span>
                        </div>
                    </div>
                ) : (
                    <div className="flex justify-center space-x-4">
                        <button
                            type="button"
                            onClick={handleRechazar}
                            className="px-6 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 transition-colors duration-200 font-medium"
                        >
                            Rechazar
                        </button>
                        <button
                            type="button"
                            onClick={handleAceptar}
                            className="px-6 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 transition-colors duration-200 font-medium"
                        >
                            Aceptar
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default CartaCompromiso;